import { useEffect, useState } from "react";
import { IModal } from "../../../../../../models/IModal";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { MdOutlineCollectionsBookmark, MdStorefront } from "react-icons/md";
import { ISelectedNode } from "../..";
import {
	ITreeNode,
	ITreeNodeData,
} from "../../../../../../models/ITreeNodeData";
import {
	BatchModalMessage,
	PendencyList,
	PendencyListBody,
	PendencyListHeader,
	PendencyListItem,
	ReadyToPublishList,
} from "./styles";
import { Modal } from "../../../../../../components/modal";
import { useTranslation } from "react-i18next";
import { TreeTablePublishable } from "./components";

interface IBatchPublishModal extends IModal {
	selection: {
		disciplines: ISelectedNode[];
		items: ISelectedNode[];
		objects: number[];
		objectsComposed: ISelectedNode[];
	};
	nodes: any[];
	action: number;
	onLocationSelect: (state: boolean) => void;
	onStoreSelect: (state: boolean) => void;
}

export const BatchActionPublishModal: React.FC<IBatchPublishModal> = ({
	selection,
	loading,
	onHide,
	onSubmit,
	show,
	variant,
	nodes,
	action,
	onLocationSelect,
	onStoreSelect,
}) => {
	const { t: translate } = useTranslation();

	const [publishToStore, setPublishToStore] = useState(false);
	const [publishToLocacao, setPublishToLocacao] = useState(false);

	/** os states hasAny... identificam se ao menos um elemento selecionado não está ativo na loja ou locação, para as labels da primeira fase do modal */
	const [hasAnyInactiveOnStore, setHasAnyInactiveOnStore] = useState(false);
	const [hasAnyInactiveOnLocacao, setHasAnyInactiveOnLocacao] =
		useState(false);

	const [nodeSelection, setNodeSelection] = useState<ITreeNode[]>();
	const [pendencies, setPendencies] = useState<ITreeNodeData[]>();
	const [canPublish, setCanPublish] = useState<ITreeNode[]>();

	// a publicação pode ter de 1 a 3 passos
	// quando não há nenhum pendência, publica após a primeira confirmação
	// quando há pendências, informa o usuário na segunda etapa quais não irão para frente,
	// e na terceira etapa, os que irão
	const [actionStep, setActionStep] = useState(0);

	/**
	 * Callbacks para setar o destino do request em lote, que é feito pelo componente pai deste aqui
	 */
	useEffect(() => {
		onLocationSelect(publishToLocacao);
		onStoreSelect(publishToStore);
	}, [publishToLocacao, publishToStore]);

	/**
	 *  Atualiza lista de materiais que serão publicados quando chego no passo final
	 */
	useEffect(() => {
		if (pendencies && pendencies.length > 0) {
			let publishList: ITreeNode[] = [];

			// adicionar somente os materiais sem pendencia a lista de publicação
			nodeSelection?.forEach((node) => {
				let hasPendency = pendencies.some(
					(pendency) => pendency?.id === node?.data?.id
				);

				if (!hasPendency) {
					publishList.push(node);
				}
			});

			setCanPublish(reNest(publishList));
		} else {
			setCanPublish(reNest(nodeSelection!));
		}
	}, [pendencies]);

	/**
	 * Re-aninho os itens selecionados, colocando objetos e unidades dentro do [children] de seus pais
	 * @returns
	 */
	const reNest = (nodeList: ITreeNode[]): ITreeNode[] => {
		let reNestedList: ITreeNode[] = [];

		// para cada node, verificar se ele é filho de alguém na lista
		if (nodeList && nodeList.length > 0) {
			// começo adicionando as disciplinas ao primeiro nível
			reNestedList.push(
				...nodeList.filter(
					(aDiscipline) => aDiscipline.data?.type === `Discipline`
				)
			);

			// unidades e temas podem ter pai
			let secondLevelItems = nodeList.filter(
				(node) =>
					node.data?.type === `Item` || node.data?.type === `Theme`
			);

			secondLevelItems.forEach((node) => {
				// procurar pela disciplina mãe, se existir, adicionar este elemento como child
				let possibleParent = reNestedList.find(
					(parent) =>
						parent.data?.id &&
						parent.data?.id === node.data?.discipline
				);

				if (possibleParent) {
					if (possibleParent.children) {
						possibleParent.children.push(node);
					} else {
						possibleParent.children = [node];
					}
				} else {
					// caso não tenha pai, vai para a root da lista
					// indico que não tem pai, para não ter identação na tabela depois
					node.independent = true;
					reNestedList.push(node);
				}
			});

			// objetos
			let thirdLevelItems = nodeList.filter(
				(node) => node.data?.type === `Media`
			);
			thirdLevelItems.forEach((node) => {
				// procurar pela disciplina mãe, se existir, procurar pela unidade mãe
				let possibleGrandParent = reNestedList.find(
					(parent) =>
						parent.data?.id &&
						parent.data?.id === node.data?.discipline
				);
				if (
					possibleGrandParent &&
					possibleGrandParent.children?.length
				) {
					// a disciplina mãe deste objeto está na lista, buscar pela unidade
					let possibleParent = possibleGrandParent.children.find(
						(parent) =>
							parent.data?.id &&
							parent.data?.id === node.data?.discipline
					);

					if (possibleParent) {
						if (possibleParent.children) {
							possibleParent.children.push(node);
						} else {
							possibleParent.children = [node];
						}
					} else {
						possibleGrandParent.children.push(node);
					}
				} else {
					// pode ser que ele esteja dentro de um tema ou unidade mas nao de uma disciplina
					let possibleParent = reNestedList.find(
						(parent) =>
							parent.data?.id &&
							parent.data?.id === node.data?.item
					);
					if (possibleParent) {
						if (possibleParent.children) {
							possibleParent.children.push(node);
						} else {
							possibleParent.children = [node];
						}
					} else {
						// indico que não tem pai, para não ter identação na tabela depois
						node.independent = true;
						reNestedList.push(node);
					}
				}
			});
		}
		return reNestedList;
	};

	// quando a selecao de itens muda, atualizo o state local
	useEffect(() => {
		updateNodeSelection();
	}, [selection, nodes]);

	// sempre que o state local muda, vejo se possui algum item não ativo na locação ou loja
	useEffect(() => {
		updateHasAnyInactiveStates();
	}, [nodeSelection]);

	// sempre que seleciona um local para publicação, atualizo as pendências de publicação
	useEffect(() => {
		updatePendencyList();
	}, [publishToLocacao, publishToStore]);

	/**
	 * Procura recursivamente por um id dentro de um node
	 */
	const hasMatchingId = (node: ITreeNode, id: number): boolean => {
		if (node.data.id === id) {
			return true;
		} else {
			if (node.children && node.children.length > 0) {
				return node.children.some((child) => hasMatchingId(child, id));
			} else {
				return false;
			}
		}
	};
	/**
	 * Extrai um node com a id indicada de dentro de outro node
	 * Quando usado em conjunto com hasMatchingId garante um resultado, caso contrário, retorna null quando a id não é encontrada
	 */
	const extractMatchingIdFrom = (
		node: ITreeNode,
		id: number
	): ITreeNode | null => {
		if (node.data.id === id) {
			return node;
		} else {
			if (node.children && node.children.length > 0) {
				let matchingNode = null;
				node.children.forEach((child) => {
					try {
						matchingNode = extractMatchingIdFrom(child, id);
					} catch (e) {}
				});
				return matchingNode;
			} else {
				throw new Error(`no matching id`);
			}
		}
	};
	/**
	 * Desce dois níveis na árvore de nodes para encontrar o pai de determianda criança
	 */
	const getNodeParent = (child: ITreeNode): ITreeNode | null => {
		for (let firstLevel in nodes as ITreeNode[]) {
			if (nodes[firstLevel].key === child.key) return null;

			if (
				nodes[firstLevel].children &&
				nodes[firstLevel].children.length > 0
			) {
				let mayHaveParentUnit = nodes[firstLevel].children.filter(
					(possibleMatch: ITreeNode) =>
						possibleMatch.key === child.key
				);

				if (mayHaveParentUnit && mayHaveParentUnit.length) {
					return nodes[firstLevel];
				} else {
					for (let secondLevel in nodes[firstLevel].children) {
						let possibleParent =
							nodes[firstLevel].children[secondLevel];

						if (
							possibleParent.children &&
							possibleParent.children.length > 0
						) {
							let mayHaveParentItem =
								possibleParent.children.filter(
									(possibleMatch: ITreeNode) =>
										possibleMatch.data.id === child.data.id
								);
							if (
								mayHaveParentItem &&
								mayHaveParentItem.length > 0
							) {
								return possibleParent;
							}
						}
					}
				}
			}
		}

		return null;
	};
	/**
	 * Extrai os nodes da tabela que estão selecionados e armazena no state nodeSelection
	 */
	const updateNodeSelection = () => {
		if (nodes && selection) {
			let newNodeSelection: any[] = [];
			// puxar os nodes de cada item dentro de selection e colocar no state nodeSelection
			if (selection.disciplines?.length > 0) {
				selection.disciplines.forEach((selectedDiscipline) => {
					// remover children do objeto - crio uma cópia
					let disciplineInNodes = nodes.find(
						(aNode) => aNode.data.id == selectedDiscipline.id
					);

					if (disciplineInNodes) {
						// veio da seleção natural
						let disciplineCopy = { ...disciplineInNodes };
						disciplineCopy.children = [];
						newNodeSelection.push(disciplineCopy);
					} else {
						// veio do selecionar todos
						newNodeSelection.push({
							key: selectedDiscipline.title,
							data: {
								id: selectedDiscipline.id,
								title: selectedDiscipline.title,
								can_publish: selectedDiscipline.can_publish,
								category: selectedDiscipline.category,
								type: "Discipline",
							},
							leaf: false,
						});
					}
				});
			}
			if (selection.items?.length > 0) {
				selection.items.forEach((selectedItem) => {
					// passar por todos nodes, checando se o id selecionado esta ali dentro, se estive, extrair
					let foundInTree = false;
					nodes.forEach((node) => {
						if (hasMatchingId(node, selectedItem.id)) {
							foundInTree = true;
							let itemInNodes = {
								...extractMatchingIdFrom(
									node,
									selectedItem.id
								)!,
							};
							itemInNodes.children = [];

							newNodeSelection.push(itemInNodes);
						}
					});

					if (!foundInTree) {
						// veio do selecionar todos
						newNodeSelection.push({
							key: selectedItem.title,
							data: {
								id: selectedItem.id,
								title: selectedItem.title,
								can_publish: selectedItem.can_publish,
								category: selectedItem.category,
								type: "Item",
							},
							leaf: false,
						});
					}
				});
			}
			if (selection.objects?.length > 0) {
				selection.objects.forEach((selectedObject) => {
					// passar por todos nodes, checando se o id selecionado esta ali dentro, se estive, extrair
					nodes.forEach((node) => {
						if (hasMatchingId(node, selectedObject)) {
							newNodeSelection.push(
								extractMatchingIdFrom(node, selectedObject)!
							);
						}
					});
				});
			}
			if (selection.objectsComposed?.length > 0) {
				selection.objectsComposed.forEach((selectedObject) => {
					let alreadyParsed = newNodeSelection.findIndex(
						(item) => item.id === selectedObject.id
					);

					if (alreadyParsed === -1) {
						newNodeSelection.push({
							key: selectedObject.title!,
							data: {
								id: selectedObject.id,
								title: selectedObject.title!,
								can_publish: selectedObject.can_publish!,
								category: selectedObject.category,
								type: "Media",
							},
							leaf: true,
						});
					}
				});
			}

			setNodeSelection(newNodeSelection);
		}
	};

	/**
	 * Atualiza o status dos hasAny... states
	 */
	const updateHasAnyInactiveStates = () => {
		if (nodeSelection?.length) {
			setHasAnyInactiveOnLocacao(
				nodeSelection.some((node) => !node.data?.is_location)
			);

			setHasAnyInactiveOnStore(
				nodeSelection.some((node) => !node.data?.is_store)
			);
		}
	};

	const reasons = (reason: string): string => {
		return (
			{
				no_price: "Material sem preço cadastrado",
				no_file: "Material sem arquivo",
			}[reason] ?? ""
		);
	};

	/**
	 * Extrai os elementos que não podem ser publicados da lista de selecionados
	 */
	const updatePendencyList = () => {
		if (nodeSelection?.length) {
			// crio um set para nao criar duplicatas
			let pendenciesList = new Set<ITreeNodeData>();

			// irá publicar
			if (action === 2) {
				// precisa de preço quando for para loja
				if (publishToStore) {
					let noPrice = nodeSelection.filter(
						(node) => !node?.data?.price
					);

					if (noPrice.length > 0) {
						noPrice.forEach((unpricedItem) => {
							if (
								unpricedItem.data?.title &&
								unpricedItem.data?.type_name
							) {
								pendenciesList.add({
									...unpricedItem.data,
									pendency_origin: "Loja",
									reason: "no_price",
								});
							}
						});
					}
				}

				// buscar nodes com reason
				nodeSelection.forEach((node) => {
					if (node.data?.reason?.length > 0) {
						pendenciesList.add({
							...node.data,
							pendency_origin: publishToLocacao
								? "Locação"
								: "Loja",
						});
					}
				});

				// quando pronta-entrega precisa de arquivo
				nodeSelection.forEach((node) => {
					if (
						node.data?.deadline === "Prompt Delivery" &&
						node.data?.leaf === false
					) {
						pendenciesList.add({
							...node.data,
							reason: "no_file",
						});
					}
				});
			} else {
				// irá despublicar
				// não pode remover unidade ou objeto que seja filho único
				// procurar em nodes pelo pai
				// ver quantos filhos o pai tem
				nodeSelection.forEach((node) => {
					let parent = getNodeParent(node);

					if (
						node?.data?.type !== `Discipline` &&
						parent &&
						parent.children!.length < 2
					) {
						pendenciesList.add({
							...node.data,
							pendency_origin: publishToStore
								? "Loja"
								: "Locação",
						});
					}
				});
			}

			setPendencies(Array.from(pendenciesList));
		}
	};

	/**
	 * Navega entre os passos do processo de publicação
	 */
	const handleStepForward = () => {
		if (publishToLocacao || publishToStore) {
			if (actionStep === 2) {
				onSubmit!();
				onHide();
			} else if (pendencies && pendencies.length > 0) {
				setActionStep(actionStep + 1);
			} else {
				// Quando não há pendências, pulo a etapa do meio
				setActionStep(2);
			}
		}
	};
	/**
	 * Navega entre os passos do processo de publicação
	 */
	const handleStepBackwards = () => {
		if (actionStep === 0) {
			onHide();
		} else if (pendencies && pendencies.length > 0) {
			setActionStep(actionStep - 1);
		} else {
			// Quando não há pendências, pulo a etapa do meio
			setActionStep(0);
		}
	};

	/**
	 * Exporta um csv com a listagem de pendências
	 */
	const handlePendenciesExport = () => {
		let csvContent = "data:text/csv;charset=utf-8,nome,motivo,local,\n";

		if (pendencies && pendencies.length > 0) {
			pendencies.forEach((item) => {
				csvContent += `${item.title},${reasons(
					item.reason
				)},${item.pendency_origin!},\n`;
			});
		}

		let encodedUri = encodeURI(csvContent);

		let link = document.createElement("a");
		link.setAttribute("href", encodedUri);
		link.setAttribute(
			"download",
			`pendências_${action === 2 ? "p" : "desp"}ublicar_lote.csv`
		);

		link.click();
	};

	return (
		<Modal
			loading={loading}
			onHide={handleStepBackwards}
			onSubmit={handleStepForward}
			hasDefaultIcon={false}
			show={show}
			variant={variant}
			headerTitle={`${action === 1 ? "Desp" : "P"}ublicar em lote`}
			submitButtonText={
				actionStep === 2
					? `${action === 1 ? "Desp" : "P"}ublicar materiais`
					: "Continuar"
			}
			cancelButtonText={actionStep > 0 ? "Voltar" : "Cancelar"}
			additionalFooterButton={
				actionStep === 1 ? (
					<Button
						variant="outline-primary"
						className="mb-1"
						onClick={handlePendenciesExport}
					>
						Exportar lista
					</Button>
				) : (
					<></>
				)
			}
		>
			{actionStep === 0 && (
				<Row>
					<Col xs={12} className="pl-0">
						<p className="text-darker-grey">
							Escolha a ação que deseja executar e clique em
							Continuar.
						</p>
					</Col>
					<Col xs={6} className="pl-0">
						<Card className="text-darker-grey card-options-publish mb-3">
							<Card.Body>
								<MdOutlineCollectionsBookmark
									className="mr-1"
									size="19"
								/>{" "}
								Locação
							</Card.Body>
						</Card>

						<Form.Check
							type="checkbox"
							className="d-flex align-items-center gap-2"
						>
							<Form.Check.Input
								id="batch-publish-to-location-check"
								type="checkbox"
								isValid
								checked={publishToLocacao}
								onChange={() =>
									setPublishToLocacao(!publishToLocacao)
								}
							/>
							<Form.Check.Label
								htmlFor="batch-publish-to-location-check"
								style={{
									color: "#606060",
									fontSize: "14px",
									marginTop: "2px",
								}}
							>
								{action === 1
									? `Despublicar o material`
									: hasAnyInactiveOnLocacao
									? `Ativar e publicar o material`
									: `Publicar o material`}
							</Form.Check.Label>
						</Form.Check>
					</Col>

					<Col xs={6} className="pr-0">
						<Card className="text-darker-grey card-options-publish mb-3">
							<Card.Body>
								<MdStorefront className="mr-1" size="19" /> Loja
							</Card.Body>
						</Card>

						<Form.Check
							type="checkbox"
							className="d-flex align-items-center gap-2"
						>
							<Form.Check.Input
								id="batch-publish-to-store-check"
								type="checkbox"
								isValid
								checked={publishToStore}
								onChange={() =>
									setPublishToStore(!publishToStore)
								}
							/>
							<Form.Check.Label
								htmlFor="batch-publish-to-store-check"
								style={{
									color: "#606060",
									fontSize: "14px",
									marginTop: "2px",
								}}
							>
								{action === 1
									? `Despublicar o material`
									: hasAnyInactiveOnStore
									? "Ativar e publicar o material"
									: "Publicar o material"}
							</Form.Check.Label>
						</Form.Check>
					</Col>
				</Row>
			)}
			{actionStep === 1 && (
				<Row className="px-0">
					<BatchModalMessage xs={12}>
						{action === 2 && (
							<>
								<h3>
									Não é possível publicar os seguintes
									materiais.
								</h3>

								<p>
									Os materiais com o prazo de pronta entrega
									precisam de arquivos para serem publicados.
									Além disso, para publicar um material na
									loja ele precisa estar precificado.
								</p>

								<p>
									Listamos abaixo os materiais que não serão
									publicados devido a falta de algum dos
									requisitos citados acima.
								</p>
							</>
						)}
						{action === 1 && (
							<>
								<h3>
									Não é possível despublicar os seguintes
									materiais.
								</h3>
								<p>
									O sistema não permite que um material fique
									vazio. Sendo assim, quando houver uma única
									unidade dentro de uma disciplina, ou um
									único objeto dentro de uma unidade, estes
									não poderão ser despublicados.
								</p>

								<p>
									Listamos abaixo os materiais que não serão
									despublicados.
								</p>
							</>
						)}
					</BatchModalMessage>

					<Col>
						<PendencyList>
							<PendencyListHeader hasReason={action === 2}>
								<span>Material</span>
								<span>Tipo</span>
								<span>Local</span>
								{action === 2 && <span>Motivo</span>}
							</PendencyListHeader>
							<PendencyListBody>
								{pendencies?.map((pendency) => (
									<PendencyListItem
										key={pendency.id}
										hasReason={action === 2}
									>
										<span>{pendency.title}</span>
										<span>
											{translate(
												`manager.type.${pendency.type.toLowerCase()}`
											)}
										</span>
										<span>{pendency.pendency_origin}</span>
										{action === 2 && (
											<span>
												{" "}
												{reasons(pendency.reason!)}
											</span>
										)}
									</PendencyListItem>
								))}
							</PendencyListBody>
						</PendencyList>
					</Col>
				</Row>
			)}
			{actionStep === 2 && (
				<Row>
					<ReadyToPublishList>
						<span>
							{pendencies && pendencies.length > 0
								? "Somente os"
								: "Os"}{" "}
							seguintes materiais serão{" "}
							{action === 1 ? "des" : ""}publicados.
						</span>

						<TreeTablePublishable
							nodes={canPublish}
						></TreeTablePublishable>
					</ReadyToPublishList>
				</Row>
			)}
		</Modal>
	);
};
